import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Truck,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Save,
} from "lucide-react";

export default function DeliveryProfile() {
  const { user } = useAuth();
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Completa todos los campos");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    setSavingPassword(true);
    try {
      await api.put("/usuarios/perfil/password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success("Contraseña actualizada correctamente");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="delivery-profile">
      <h1 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-bold">
        Mi Perfil
      </h1>

      {/* Avatar y nombre */}
      <div className="bg-white rounded-xl p-6 border border-[#E5D4B3] text-center">
        <div className="w-20 h-20 bg-[#C8902A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Truck className="w-10 h-10 text-[#C8902A]" />
        </div>
        <h2 className="font-semibold text-[#4A2C0A] text-lg">{user?.nombre}</h2>
        <span className="inline-block px-3 py-1 bg-[#C8902A]/10 text-[#C8902A] rounded-full text-sm mt-2 capitalize">
          {user?.rol}
        </span>
      </div>

      {/* Información de contacto */}
      <div className="bg-white rounded-xl p-4 border border-[#E5D4B3] space-y-4">
        <h3 className="font-semibold text-[#4A2C0A]">Información de Contacto</h3>
        
        <div className="flex items-center gap-3 text-[#4A2C0A]">
          <Mail className="w-5 h-5 text-[#4A2C0A]/40" />
          <span>{user?.email}</span>
        </div>
        
        {user?.telefono && (
          <div className="flex items-center gap-3 text-[#4A2C0A]">
            <Phone className="w-5 h-5 text-[#4A2C0A]/40" />
            <span>{user?.telefono}</span>
          </div>
        )}
        
        {user?.zona && (
          <div className="flex items-center gap-3 text-[#4A2C0A]">
            <MapPin className="w-5 h-5 text-[#4A2C0A]/40" />
            <span>{user?.zona}</span>
          </div>
        )}
      </div>

      {/* Cambio de Contraseña */}
      <form onSubmit={handleChangePassword} className="bg-white rounded-xl p-4 border border-[#E5D4B3] space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Lock className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-[#4A2C0A]">Cambiar Contraseña</h3>
            <p className="text-[#4A2C0A]/60 text-sm">Actualiza tu contraseña de acceso</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm text-[#4A2C0A]">Contraseña Actual</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Tu contraseña actual"
                className="h-11 pr-10 border-[#E5D4B3]"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#4A2C0A]">Nueva Contraseña</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                className="h-11 pr-10 border-[#E5D4B3]"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#4A2C0A]">Confirmar Nueva Contraseña</Label>
            <Input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              placeholder="Repite la nueva contraseña"
              className="h-11 border-[#E5D4B3]"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={savingPassword}
          className="w-full h-11 bg-red-600 hover:bg-red-700"
        >
          {savingPassword ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Actualizando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Cambiar Contraseña
            </span>
          )}
        </Button>
      </form>

      {/* Información de seguridad */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800">Restricciones de Seguridad</h3>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• Solo puedes ver pedidos asignados a ti</li>
              <li>• No puedes modificar precios ni productos</li>
              <li>• No puedes cambiar fechas de entrega</li>
              <li>• No puedes cambiar métodos de pago</li>
              <li>• Todas las acciones quedan registradas</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Versión */}
      <p className="text-center text-xs text-[#4A2C0A]/40">
        Baruj Shefa - Sistema de Entregas v1.0
      </p>
    </div>
  );
}
